<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header px-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0">
            <i :class="roleMeta.icon + ' me-2'"></i>{{ roleMeta.title }}
          </h4>
        </div>
        <div class="d-flex gap-2 align-items-center">
          <select v-model="role" class="hms-input" @change="loadAll" style="min-width:280px">
            <option value="governor">Governor — County overview</option>
            <option value="cec">CEC Health — Policy &amp; programmes</option>
            <option value="co_health">CO Health — Operations</option>
            <option value="medsup">Medical Superintendent — Facility</option>
          </select>
          <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- BANNER -->
      <div class="hms-exec-banner" :class="'role-' + role">
        <i :class="roleMeta.icon"></i>
        <div>
          <div class="hms-banner-title">{{ roleMeta.title }}</div>
          <div class="hms-banner-sub">{{ roleMeta.scope }}</div>
        </div>
      </div>

      <div class="px-3">
        <!-- ===== HEADLINE KPIs ===== -->
        <div class="hms-grid-4 mb-3">
          <div class="hms-kpi">
            <div class="hms-kpi-icon hms-kpi-blue"><i class="mdi mdi-account-multiple"></i></div>
            <div>
              <div class="hms-kpi-num">{{ formatNumber(d.services.visits_today || 0) }}</div>
              <div class="hms-kpi-lbl">Visits today</div>
              <div class="hms-kpi-sub">{{ formatNumber(d.services.visits_30d || 0) }} in 30d</div>
            </div>
          </div>
          <div class="hms-kpi">
            <div class="hms-kpi-icon hms-kpi-amber"><i class="mdi mdi-bed"></i></div>
            <div>
              <div class="hms-kpi-num">{{ d.services.admitted_now || 0 }}</div>
              <div class="hms-kpi-lbl">Admitted now</div>
              <div class="hms-kpi-sub">{{ formatNumber(d.services.deliveries_30d || 0) }} deliveries (30d)</div>
            </div>
          </div>
          <div class="hms-kpi">
            <div class="hms-kpi-icon hms-kpi-green"><i class="mdi mdi-cash-multiple"></i></div>
            <div>
              <div class="hms-kpi-num">KSh {{ formatNumber(d.revenue.billed_30d || 0) }}</div>
              <div class="hms-kpi-lbl">Billed 30d</div>
              <div class="hms-kpi-sub">SHA approved: KSh {{ formatNumber(d.revenue.sha_approved_30d || 0) }}</div>
            </div>
          </div>
          <div class="hms-kpi">
            <div class="hms-kpi-icon hms-kpi-purple"><i class="mdi mdi-account-tie"></i></div>
            <div>
              <div class="hms-kpi-num">{{ d.workforce.staff_active || 0 }}</div>
              <div class="hms-kpi-lbl">Active staff</div>
              <div class="hms-kpi-sub">{{ d.workforce.doctors || 0 }} drs · {{ d.workforce.nurses || 0 }} nurses</div>
            </div>
          </div>
        </div>

        <div class="hms-grid-3 mb-3">
          <!-- DISEASE PROGRAMMES -->
          <div class="hms-card">
            <div class="hms-card-head">Disease programmes</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span><i class="mdi mdi-ribbon text-danger"></i> ART current</span><strong>{{ formatNumber(d.programmes.art_active || 0) }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-lungs"></i> TB on treatment</span><strong>{{ formatNumber(d.programmes.tb_on_tx || 0) }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-needle"></i> Immunization (30d)</span><strong>{{ formatNumber(d.programmes.immunizations_30d || 0) }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-mother-heart"></i> ANC visits (30d)</span><strong>{{ formatNumber(d.programmes.anc_visits_30d || 0) }}</strong></div>
            </div>
          </div>

          <!-- COMMUNITY (governor / CEC / CO only) -->
          <div class="hms-card" v-if="d.community">
            <div class="hms-card-head">Community Health</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Active CHUs</span><strong>{{ d.community.chu_count || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Active CHPs</span><strong>{{ d.community.chp_count || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Households registered</span><strong>{{ formatNumber(d.community.hh_registered || 0) }}</strong></div>
              <div class="hms-ind-row"><span>Household visits (30d)</span><strong>{{ formatNumber(d.community.visits_30d || 0) }}</strong></div>
            </div>
          </div>

          <!-- REVENUE -->
          <div class="hms-card">
            <div class="hms-card-head">Revenue snapshot</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Billed today</span><strong>KSh {{ formatNumber(d.revenue.billed_today || 0) }}</strong></div>
              <div class="hms-ind-row"><span>Billed (30d)</span><strong>KSh {{ formatNumber(d.revenue.billed_30d || 0) }}</strong></div>
              <div class="hms-ind-row"><span>SHA approved (30d)</span><strong>KSh {{ formatNumber(d.revenue.sha_approved_30d || 0) }}</strong></div>
              <div class="hms-ind-row"><span>SHA claims pending</span><strong>{{ d.revenue.claims_pending || 0 }}</strong></div>
            </div>
          </div>
        </div>

        <!-- SUB-COUNTY ROLLUP -->
        <div v-if="d.by_sub_county && d.by_sub_county.length" class="hms-card mb-3">
          <div class="hms-card-head">By sub-county — {{ d.by_sub_county.length }} sub-counties</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>Sub-county</th><th>Facilities</th><th>Beds</th><th>Catchment pop.</th><th>Visits today</th><th>Deliveries (30d)</th><th>Revenue (30d)</th></tr></thead>
              <tbody>
                <tr v-for="s in d.by_sub_county" :key="s.sub_county">
                  <td><strong>{{ s.sub_county }}</strong></td>
                  <td>{{ s.facility_count }}</td>
                  <td>{{ formatNumber(s.beds) }}</td>
                  <td>{{ formatNumber(s.catchment) }}</td>
                  <td>{{ formatNumber(s.visits_today) }}</td>
                  <td>{{ formatNumber(s.deliveries_30d) }}</td>
                  <td>KSh {{ formatNumber(s.revenue_30d) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- BY-BRANCH BREAKDOWN (governor / CEC / CO) -->
        <div v-if="d.by_branch && d.by_branch.length" class="hms-card">
          <div class="hms-card-head">By facility — {{ d.by_branch.length }} facilities</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead>
                <tr>
                  <th>Facility</th><th>Level</th>
                  <th>Visits today</th><th>Admitted</th>
                  <th>Deliveries (30d)</th><th>Revenue (30d)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in d.by_branch" :key="b.branch_id">
                  <td><strong>{{ b.branch_name }}</strong></td>
                  <td><span class="hms-pill" :class="lvlPill(b.facility_level)">L{{ b.facility_level }}</span></td>
                  <td>{{ formatNumber(b.visits_today) }}</td>
                  <td>{{ formatNumber(b.admitted) }}</td>
                  <td>{{ formatNumber(b.deliveries_30d) }}</td>
                  <td>KSh {{ formatNumber(b.revenue_30d) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const ROLES = {
  governor:   { title: "Governor's Dashboard",            icon: "mdi mdi-account-tie-hat",      scope: "Countywide health overview — all 6 facilities + community" },
  cec:        { title: "CEC Health Dashboard",            icon: "mdi mdi-briefcase-account",    scope: "Policy & programme indicators — disease programmes, equity, community" },
  co_health:  { title: "CO Health Dashboard",             icon: "mdi mdi-account-cog",          scope: "Operations — service volume, revenue, workforce, by-facility breakdown" },
  medsup:     { title: "Medical Superintendent Dashboard",icon: "mdi mdi-stethoscope",          scope: "Facility-level operations — your facility only" }
};

export default {
  name: "ExecutiveDashboard",
  components: { Navigation },
  data() {
    return {
      role: 'governor',
      d: { services: {}, programmes: {}, revenue: {}, workforce: {}, community: null, by_branch: [], by_sub_county: [] }
    };
  },
  computed: {
    roleMeta() { return ROLES[this.role] || ROLES.medsup; }
  },
  mounted() { this.loadAll(); },
  methods: {
    async loadAll() {
      try {
        const r = await executeJson({ function: 'getExecutiveDashboard', role: this.role }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.d = {
            services:   d.services   || {},
            programmes: d.programmes || {},
            revenue:    d.revenue    || {},
            workforce:  d.workforce  || {},
            community:  d.community  || null,
            by_branch:  d.by_branch  || [],
            by_sub_county: d.by_sub_county || []
          };
        }
      } catch (e) { console.error(e); }
    },
    formatNumber(n) { const x = parseInt(n || 0); return x.toLocaleString(); },
    lvlPill(l) {
      const lv = parseInt(l || 4);
      if (lv >= 5) return 'hms-pill-danger';
      if (lv === 4) return 'hms-pill-warn';
      return 'hms-pill-info';
    }
  }
};
</script>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 0 40px; }
.hms-back { color: var(--hms-s); font-size: 20px; text-decoration: none; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { font-size: 18px; font-weight: 600; color: var(--hms-h); }

.hms-exec-banner { display: flex; align-items: center; gap: 16px; padding: 18px 22px; border-radius: 10px; color: #fff; margin: 0 12px 16px; }
.hms-exec-banner .mdi { font-size: 36px; }
.hms-banner-title { font-size: 18px; font-weight: 700; }
.hms-banner-sub   { font-size: 13px; opacity: .85; }
.hms-exec-banner.role-governor   { background: linear-gradient(120deg, #1e3a8a, #1d4ed8); }
.hms-exec-banner.role-cec        { background: linear-gradient(120deg, #166534, #16a34a); }
.hms-exec-banner.role-co_health  { background: linear-gradient(120deg, #b45309, #d97706); }
.hms-exec-banner.role-medsup     { background: linear-gradient(120deg, #6b21a8, #9333ea); }

.hms-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.hms-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
@media (max-width: 1100px) {
  .hms-grid-4 { grid-template-columns: repeat(2, 1fr); }
  .hms-grid-3 { grid-template-columns: 1fr; }
}

.hms-kpi { display: flex; align-items: center; gap: 14px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; }
.hms-kpi-icon { width: 52px; height: 52px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 24px; }
.hms-kpi-blue   { background: #ebf8ff; color: #2b6cb0; }
.hms-kpi-amber  { background: #fffaf0; color: #b45309; }
.hms-kpi-green  { background: #f0fff4; color: #276749; }
.hms-kpi-purple { background: #faf5ff; color: #6b21a8; }
.hms-kpi-num    { font-size: 24px; font-weight: 700; color: #1a202c; }
.hms-kpi-lbl    { font-size: 12px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }
.hms-kpi-sub    { font-size: 12px; color: #4a5568; margin-top: 2px; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.hms-ind-row:last-child { border-bottom: none; }
.hms-ind-row strong { color: #2d3748; font-size: 16px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hms-table th { background: #f7fafc; padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 9px 10px; border-bottom: 1px solid #edf2f7; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; }
.hms-pill-info   { background: #bee3f8; color: #2a4365; }
.hms-pill-warn   { background: #feebc8; color: #7b341e; }
.hms-pill-danger { background: #fed7d7; color: #742a2a; }

.hms-input { padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }
.text-danger { color: #c53030; }
</style>
